import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

const API_BASE = process.env.REACT_APP_API_BASE || "";

const getBandConfig = (band) => {
  const normalized = String(band || "").toUpperCase();

  if (normalized === "HIGH") {
    return {
      label: "HIGH",
      bg: "#dcfce7",
      text: "#166534",
      border: "#86efac",
      rowBg: "#f0fdf4",
      accent: "#22c55e",
    };
  }

  if (normalized === "MEDIUM") {
    return {
      label: "MEDIUM",
      bg: "#ffedd5",
      text: "#9a3412",
      border: "#fed7aa",
      rowBg: "#fff7ed",
      accent: "#f97316",
    };
  }

  if (normalized === "LOW") {
    return {
      label: "LOW",
      bg: "#fee2e2",
      text: "#991b1b",
      border: "#fecaca",
      rowBg: "#fef2f2",
      accent: "#ef4444",
    };
  }

  return {
    label: normalized || "-",
    bg: "#f1f5f9",
    text: "#334155",
    border: "#cbd5e1",
    rowBg: "#ffffff",
    accent: "#64748b",
  };
};

const formatNumber = (value, digits = 2) => {
  if (value === null || value === undefined || value === "") return "-";
  const num = Number(value);
  if (Number.isNaN(num)) return "-";
  return num.toFixed(digits);
};

const formatArea = (value) => {
  if (!value) return "-";
  return Math.round(Number(value)).toLocaleString();
};

function BandPill({ band }) {
  const config = getBandConfig(band);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 10px",
        borderRadius: 999,
        background: config.bg,
        color: config.text,
        border: `1px solid ${config.border}`,
        fontWeight: 800,
        fontSize: 12,
        letterSpacing: "0.03em",
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: config.accent,
        }}
      />
      {config.label}
    </span>
  );
}

function ScoreBadge({ score }) {
  const value = Number(score || 0);
  const pct = Math.max(0, Math.min(100, value * 10));

  return (
    <div style={{ minWidth: 92 }}>
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 4,
          marginBottom: 6,
        }}
      >
        <span style={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>
          {formatNumber(value, 2)}
        </span>
        <span style={{ fontSize: 11, color: "#64748b", fontWeight: 700 }}>
          / 10
        </span>
      </div>

      <div
        style={{
          height: 7,
          width: 82,
          borderRadius: 999,
          background: "#e2e8f0",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${pct}%`,
            borderRadius: 999,
            background:
              value >= 7
                ? "linear-gradient(90deg, #22c55e, #16a34a)"
                : value >= 4
                  ? "linear-gradient(90deg, #f97316, #ea580c)"
                  : "linear-gradient(90deg, #ef4444, #dc2626)",
          }}
        />
      </div>
    </div>
  );
}

function BreakdownChips({ breakdown }) {
  const items = [
    ["Corridor", breakdown?.corridor ?? 0],
    ["Greenland", breakdown?.greenland ?? 0],
    ["SRT", breakdown?.srt ?? 0],
    ["Bus", breakdown?.bus ?? 0],
    ["Facilities", breakdown?.facilities ?? 0],
  ];

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, minWidth: 220 }}>
      {items.map(([label, value]) => (
        <span
          key={label}
          style={{
            display: "inline-flex",
            gap: 5,
            alignItems: "center",
            padding: "5px 8px",
            borderRadius: 999,
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            color: "#334155",
            fontSize: 12,
            whiteSpace: "nowrap",
          }}
        >
          <b style={{ color: "#0f172a" }}>{label}</b>
          {formatNumber(value, 2).replace(/\.00$/, "")}
        </span>
      ))}
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div style={styles.statCard}>
      <div style={styles.statLabel}>{label}</div>
      <div style={styles.statValue}>{value}</div>
      {hint ? <div style={styles.statHint}>{hint}</div> : null}
    </div>
  );
}

function SkeletonRows() {
  return (
    <div style={{ padding: 18 }}>
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} style={styles.skeletonRow}>
          <div style={{ ...styles.skeletonLine, width: "8%" }} />
          <div style={{ ...styles.skeletonLine, width: "12%" }} />
          <div style={{ ...styles.skeletonLine, width: "16%" }} />
          <div style={{ ...styles.skeletonLine, width: "24%" }} />
          <div style={{ ...styles.skeletonLine, width: "18%" }} />
        </div>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const [rows, setRows] = useState([]);
  const [filters, setFilters] = useState({
    municipalities: [],
    types: [],
    statuses: [],
  });

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);

  const [sort, setSort] = useState("-score");
  const [status, setStatus] = useState("");
  const [mun, setMun] = useState(searchParams.get("mun") || "");
  const [type, setType] = useState("");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => {
    if (!total || !pageSize) return 1;
    return Math.max(1, Math.ceil(total / pageSize));
  }, [total, pageSize]);

  const averageScore = useMemo(() => {
    if (!rows.length) return "-";
    const sum = rows.reduce((acc, row) => acc + Number(row.score || 0), 0);
    return formatNumber(sum / rows.length, 2);
  }, [rows]);

  const highCount = useMemo(
    () => rows.filter((row) => String(row.band).toUpperCase() === "HIGH").length,
    [rows]
  );

  useEffect(() => {
    setError("");
    setLoadingFilters(true);

    fetch(`${API_BASE}/api/analysis/dashboard/filters/`)
      .then((r) => {
        if (!r.ok) throw new Error(`Filters HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        setFilters({
          municipalities: d.municipalities || [],
          types: d.types || [],
          statuses: d.statuses || [],
        });
      })
      .catch((e) => setError(e.message || "Failed to load filters"))
      .finally(() => setLoadingFilters(false));
  }, []);

  useEffect(() => {
    setLoading(true);
    setError("");

    const params = new URLSearchParams();
    params.set("sort", sort);
    params.set("page", String(page));
    params.set("page_size", String(pageSize));

    if (mun) params.set("mun", mun);
    if (type) params.set("type", type);
    if (status) params.set("status", status);

    fetch(`${API_BASE}/api/analysis/dashboard/apps/?${params.toString()}`)
      .then((r) => {
        if (!r.ok) throw new Error(`Apps HTTP ${r.status}`);
        return r.json();
      })
      .then((d) => {
        setRows(d.results || []);
        setTotal(d.total || 0);
      })
      .catch((e) => setError(e.message || "Failed to load apps"))
      .finally(() => setLoading(false));
  }, [sort, mun, type, status, page, pageSize]);

  useEffect(() => {
    const munFromUrl = searchParams.get("mun") || "";
    setMun(munFromUrl);
  }, [searchParams]);

  const onClearFilters = () => {
    setMun("");
    setType("");
    setStatus("");
    setSort("-score");
    setPage(1);
  };

  const canPrev = page > 1;
  const canNext = page < totalPages;

  return (
    <div style={styles.page}>
      <style>
        {`
          @keyframes dashboardPulse {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }

          .dashboard-table-row:hover {
            transform: translateY(-1px);
            box-shadow: 0 12px 24px rgba(15, 23, 42, 0.08);
            z-index: 1;
          }

          .dashboard-select:focus,
          .dashboard-button:focus {
            outline: 3px solid rgba(249, 115, 22, 0.18);
            border-color: #f97316;
          }
        `}
      </style>

      <div style={styles.container}>
        <div style={styles.hero}>
          <div>
            <div style={styles.eyebrow}>UrbanScore Dashboard</div>
            <h1 style={styles.title}>York Applications Dashboard</h1>
            <p style={styles.subtitle}>
              Review, filter, and prioritize development applications based on parcel score and planning indicators.
            </p>
          </div>

          <div style={styles.heroMeta}>
            <div style={styles.totalText}>
              Total <b>{total}</b>
            </div>
            <div style={styles.pageText}>
              Page <b>{page}</b> / <b>{totalPages}</b>
            </div>
          </div>
        </div>

        <div style={styles.statsGrid}>
          <StatCard label="Applications" value={total.toLocaleString()} hint="matching current filters" />
          <StatCard label="Showing" value={rows.length.toLocaleString()} hint={`page size ${pageSize}`} />
          <StatCard label="Average Score" value={averageScore} hint="current page" />
          <StatCard label="High Band" value={highCount} hint="current page" />
        </div>

        <div style={styles.filterCard}>
          <div style={styles.filterGrid}>
            <label style={styles.field}>
              <span style={styles.fieldLabel}>Sort</span>
              <select
                className="dashboard-select"
                value={sort}
                onChange={(e) => {
                  setSort(e.target.value);
                  setPage(1);
                }}
                style={styles.select}
              >
                <option value="-score">Score — High to Low</option>
                <option value="score">Score — Low to High</option>
              </select>
            </label>

            <label style={styles.field}>
              <span style={styles.fieldLabel}>Municipality</span>
              <select
                className="dashboard-select"
                value={mun}
                onChange={(e) => {
                  setMun(e.target.value);
                  setPage(1);
                }}
                style={styles.select}
                disabled={loadingFilters}
              >
                <option value="">All Municipalities</option>
                {filters.municipalities.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.field}>
              <span style={styles.fieldLabel}>Application Type</span>
              <select
                className="dashboard-select"
                value={type}
                onChange={(e) => {
                  setType(e.target.value);
                  setPage(1);
                }}
                style={styles.select}
                disabled={loadingFilters}
              >
                <option value="">All Types</option>
                {filters.types.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.field}>
              <span style={styles.fieldLabel}>Status</span>
              <select
                className="dashboard-select"
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(1);
                }}
                style={styles.select}
                disabled={loadingFilters}
              >
                <option value="">All Statuses</option>
                {filters.statuses.map((x) => (
                  <option key={x} value={x}>
                    {x}
                  </option>
                ))}
              </select>
            </label>

            <label style={styles.fieldSmall}>
              <span style={styles.fieldLabel}>Page Size</span>
              <select
                className="dashboard-select"
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                style={styles.select}
              >
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>

            <button className="dashboard-button" onClick={onClearFilters} style={styles.clearButton}>
              Clear Filters
            </button>
          </div>
        </div>

        {error ? (
          <div style={styles.errorBox}>
            <b>Error:</b> {error}
          </div>
        ) : null}

        <div style={styles.tableCard}>
          <div style={styles.tableToolbar}>
            <div>
              <h2 style={styles.tableTitle}>Application Ranking</h2>
              <div style={styles.tableSub}>
                Showing <b>{rows.length}</b> of <b>{total}</b> applications
              </div>
            </div>

            <div style={styles.pagination}>
              <button
                className="dashboard-button"
                disabled={!canPrev}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                style={{
                  ...styles.pageButton,
                  ...(!canPrev ? styles.disabledButton : {}),
                }}
              >
                ← Previous
              </button>

              <span style={styles.pagePill}>
                {page} / {totalPages}
              </span>

              <button
                className="dashboard-button"
                disabled={!canNext}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                style={{
                  ...styles.pageButton,
                  ...(!canNext ? styles.disabledButton : {}),
                }}
              >
                Next →
              </button>
            </div>
          </div>

          {loading ? (
            <SkeletonRows />
          ) : (
            <div style={styles.tableScroll}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {[
                      "Rank",
                      "Band",
                      "Score",
                      "SforceID",
                      "Municipality",
                      "Type",
                      "Status",
                      "Area",
                      "Address",
                      "Breakdown",
                    ].map((h) => (
                      <th key={h} style={styles.th}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {rows.map((r) => {
                    const bandConfig = getBandConfig(r.band);

                    return (
                      <tr
                        key={r.sforce_id}
                        className="dashboard-table-row"
                        style={{
                          ...styles.tr,
                          background: bandConfig.rowBg,
                          borderLeft: `5px solid ${bandConfig.accent}`,
                        }}
                      >
                        <td style={styles.td}>
                          <span style={styles.rankBadge}>{r.rank}</span>
                        </td>

                        <td style={styles.td}>
                          <BandPill band={r.band} />
                        </td>

                        <td style={styles.td}>
                          <ScoreBadge score={r.score} />
                        </td>

                        <td style={styles.td}>
                          <button
                            onClick={() => setSelected(r)}
                            style={styles.linkButton}
                            title="Open map preview"
                          >
                            {r.sforce_id}
                          </button>
                        </td>

                        <td style={styles.td}>
                          <div style={styles.primaryText}>{r.municipality || "-"}</div>
                        </td>

                        <td style={styles.td}>
                          <span style={styles.typePill}>{r.application_type || "-"}</span>
                        </td>

                        <td style={styles.td}>
                          <span style={styles.statusPill}>{r.status || "-"}</span>
                        </td>

                        <td style={styles.td}>
                          <div style={styles.primaryText}>{formatArea(r.area)}</div>
                        </td>

                        <td style={{ ...styles.td, minWidth: 320 }}>
                          <div style={styles.addressText} title={r.address || ""}>
                            {r.address || "-"}
                          </div>
                        </td>

                        <td style={styles.td}>
                          <BreakdownChips breakdown={r.breakdown} />
                        </td>
                      </tr>
                    );
                  })}

                  {!rows.length && !loading && (
                    <tr>
                      <td colSpan={10} style={styles.emptyCell}>
                        No applications found for the selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div style={styles.footerPagination}>
            <button
              className="dashboard-button"
              disabled={!canPrev}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              style={{
                ...styles.pageButton,
                ...(!canPrev ? styles.disabledButton : {}),
              }}
            >
              ← Previous
            </button>

            <div style={styles.footerPageText}>
              Page <b>{page}</b> of <b>{totalPages}</b>
            </div>

            <button
              className="dashboard-button"
              disabled={!canNext}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              style={{
                ...styles.pageButton,
                ...(!canNext ? styles.disabledButton : {}),
              }}
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {selected && (
        <div onClick={() => setSelected(null)} style={styles.modalOverlay}>
          <div onClick={(e) => e.stopPropagation()} style={styles.modal}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalEyebrow}>Application Preview</div>
                <h3 style={styles.modalTitle}>{selected.sforce_id}</h3>
                <div style={styles.modalSub}>
                  {selected.municipality || "-"} • {selected.application_type || "-"}
                </div>
              </div>

              <button
                onClick={() => setSelected(null)}
                style={styles.closeButton}
                aria-label="Close"
                title="Close"
              >
                ×
              </button>
            </div>

            <div style={styles.modalBody}>
              {selected.centroid_lat && selected.centroid_lng ? (
                <div style={styles.mapFrame}>
                  <iframe
                    title="map"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://www.google.com/maps?q=${selected.centroid_lat},${selected.centroid_lng}&z=15&output=embed`}
                  />
                </div>
              ) : (
                <div style={styles.warningBox}>No centroid available for this project.</div>
              )}

              <div style={styles.modalStats}>
                <StatCard label="Status" value={selected.status || "-"} />
                <StatCard label="Score" value={formatNumber(selected.score, 2)} />
                <StatCard label="Band" value={selected.band || "-"} />
                <StatCard label="Area" value={formatArea(selected.area)} />
              </div>

              {selected.address ? (
                <div style={styles.addressBox}>
                  <b>Address:</b> {selected.address}
                </div>
              ) : null}

              <div style={styles.modalActions}>
                <button onClick={() => setSelected(null)} style={styles.secondaryButton}>
                  Close
                </button>

                <button
                  onClick={() =>
                    navigate(`/projects/${selected.sforce_id}`, {
                      state: {
                        centroid: {
                          lat: selected.centroid_lat,
                          lng: selected.centroid_lng,
                        },
                      },
                    })
                  }
                  style={styles.primaryButton}
                >
                  Open Details →
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top left, rgba(249,115,22,0.10), transparent 28%), #f8fafc",
    color: "#0f172a",
    fontFamily:
      'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },

  container: {
    width: "min(1600px, calc(100% - 32px))",
    margin: "0 auto",
    padding: "24px 0 36px",
  },

  hero: {
    display: "flex",
    justifyContent: "space-between",
    gap: 18,
    flexWrap: "wrap",
    alignItems: "flex-start",
    marginBottom: 18,
  },

  eyebrow: {
    color: "#f97316",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.1em",
    textTransform: "uppercase",
    marginBottom: 8,
  },

  title: {
    margin: 0,
    fontSize: 34,
    lineHeight: 1.05,
    letterSpacing: "-0.04em",
  },

  subtitle: {
    margin: "10px 0 0",
    color: "#64748b",
    fontSize: 15,
    maxWidth: 720,
    lineHeight: 1.7,
  },

  heroMeta: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
  },

  totalText: {
    padding: "10px 14px",
    borderRadius: 16,
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    boxShadow: "0 10px 24px rgba(15,23,42,0.05)",
    color: "#475569",
  },

  pageText: {
    padding: "10px 14px",
    borderRadius: 16,
    background: "#0f172a",
    color: "#ffffff",
    boxShadow: "0 10px 24px rgba(15,23,42,0.10)",
  },

  statsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 14,
    marginBottom: 16,
  },

  statCard: {
    background: "rgba(255,255,255,0.9)",
    border: "1px solid #e2e8f0",
    borderRadius: 20,
    padding: 16,
    boxShadow: "0 12px 28px rgba(15,23,42,0.05)",
  },

  statLabel: {
    color: "#64748b",
    fontSize: 12,
    fontWeight: 800,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },

  statValue: {
    marginTop: 8,
    color: "#0f172a",
    fontSize: 24,
    fontWeight: 900,
    lineHeight: 1,
  },

  statHint: {
    marginTop: 8,
    color: "#94a3b8",
    fontSize: 12,
  },

  filterCard: {
    background: "rgba(255,255,255,0.92)",
    border: "1px solid #e2e8f0",
    borderRadius: 24,
    padding: 16,
    boxShadow: "0 12px 28px rgba(15,23,42,0.05)",
    marginBottom: 16,
  },

  filterGrid: {
    display: "grid",
    gridTemplateColumns:
      "minmax(180px, 1fr) minmax(220px, 1.2fr) minmax(240px, 1.4fr) minmax(180px, 1fr) 120px 140px",
    gap: 12,
    alignItems: "end",
  },

  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  fieldSmall: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },

  fieldLabel: {
    fontSize: 12,
    fontWeight: 800,
    color: "#475569",
  },

  select: {
    width: "100%",
    height: 42,
    borderRadius: 14,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    padding: "0 12px",
    fontSize: 14,
    fontWeight: 600,
  },

  clearButton: {
    height: 42,
    borderRadius: 14,
    border: "1px solid #fed7aa",
    background: "#fff7ed",
    color: "#c2410c",
    fontWeight: 800,
    cursor: "pointer",
  },

  errorBox: {
    padding: 14,
    border: "1px solid #fecaca",
    borderRadius: 16,
    background: "#fef2f2",
    color: "#991b1b",
    marginBottom: 16,
  },

  tableCard: {
    background: "#ffffff",
    border: "1px solid #e2e8f0",
    borderRadius: 24,
    overflow: "hidden",
    boxShadow: "0 18px 44px rgba(15,23,42,0.08)",
  },

  tableToolbar: {
    padding: 18,
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    flexWrap: "wrap",
    alignItems: "center",
    borderBottom: "1px solid #e2e8f0",
    background:
      "linear-gradient(180deg, rgba(248,250,252,1), rgba(255,255,255,1))",
  },

  tableTitle: {
    margin: 0,
    fontSize: 21,
    letterSpacing: "-0.02em",
  },

  tableSub: {
    marginTop: 5,
    color: "#64748b",
    fontSize: 13,
  },

  pagination: {
    display: "flex",
    gap: 8,
    alignItems: "center",
  },

  pageButton: {
    height: 38,
    padding: "0 13px",
    borderRadius: 12,
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    color: "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
  },

  disabledButton: {
    opacity: 0.45,
    cursor: "not-allowed",
  },

  pagePill: {
    height: 38,
    minWidth: 72,
    padding: "0 12px",
    borderRadius: 12,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#475569",
    fontWeight: 800,
  },

  tableScroll: {
    overflowX: "auto",
  },

  table: {
    width: "100%",
    minWidth: 1280,
    borderCollapse: "separate",
    borderSpacing: "0 10px",
    padding: "0 14px 14px",
  },

  th: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    textAlign: "left",
    padding: "14px 10px",
    fontSize: 12,
    color: "#64748b",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    background: "#ffffff",
    borderBottom: "1px solid #e2e8f0",
    whiteSpace: "nowrap",
  },

  tr: {
    position: "relative",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    boxShadow: "0 4px 12px rgba(15,23,42,0.04)",
  },

  td: {
    padding: "15px 10px",
    borderTop: "1px solid #e2e8f0",
    borderBottom: "1px solid #e2e8f0",
    verticalAlign: "middle",
    fontSize: 14,
  },

  rankBadge: {
    width: 34,
    height: 34,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 12,
    background: "#0f172a",
    color: "#ffffff",
    fontWeight: 900,
  },

  primaryText: {
    color: "#0f172a",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },

  typePill: {
    display: "inline-flex",
    padding: "6px 10px",
    borderRadius: 999,
    background: "#eef2ff",
    color: "#3730a3",
    border: "1px solid #c7d2fe",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },

  statusPill: {
    display: "inline-flex",
    padding: "6px 10px",
    borderRadius: 999,
    background: "#f8fafc",
    color: "#334155",
    border: "1px solid #cbd5e1",
    fontWeight: 700,
    whiteSpace: "nowrap",
  },

  addressText: {
    maxWidth: 420,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    color: "#334155",
    fontWeight: 600,
  },

  linkButton: {
    border: "none",
    background: "transparent",
    color: "#2563eb",
    cursor: "pointer",
    padding: 0,
    font: "inherit",
    fontWeight: 800,
    textDecoration: "none",
    borderBottom: "1px solid rgba(37,99,235,0.35)",
    whiteSpace: "nowrap",
  },

  emptyCell: {
    padding: 30,
    textAlign: "center",
    color: "#64748b",
  },

  footerPagination: {
    padding: 16,
    borderTop: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: 10,
    background: "#f8fafc",
  },

  footerPageText: {
    color: "#475569",
    fontSize: 14,
  },

  skeletonRow: {
    height: 66,
    display: "flex",
    alignItems: "center",
    gap: 18,
    borderBottom: "1px solid #eef2f7",
  },

  skeletonLine: {
    height: 14,
    borderRadius: 999,
    background:
      "linear-gradient(90deg, rgba(15,23,42,0.05), rgba(15,23,42,0.10), rgba(15,23,42,0.05))",
    backgroundSize: "200% 100%",
    animation: "dashboardPulse 1.2s ease-in-out infinite",
  },

  modalOverlay: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.55)",
    backdropFilter: "blur(8px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: 18,
  },

  modal: {
    width: "min(1040px, 100%)",
    maxHeight: "calc(100vh - 36px)",
    overflow: "hidden",
    background: "#ffffff",
    borderRadius: 24,
    boxShadow: "0 30px 80px rgba(15,23,42,0.35)",
    border: "1px solid rgba(255,255,255,0.7)",
    display: "flex",
    flexDirection: "column",
  },

  modalHeader: {
    padding: 18,
    borderBottom: "1px solid #e2e8f0",
    display: "flex",
    justifyContent: "space-between",
    gap: 14,
    alignItems: "flex-start",
    background:
      "linear-gradient(135deg, rgba(249,115,22,0.10), rgba(255,255,255,1))",
  },

  modalEyebrow: {
    color: "#f97316",
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
  },

  modalTitle: {
    margin: "5px 0 0",
    fontSize: 24,
  },

  modalSub: {
    marginTop: 5,
    color: "#64748b",
  },

  closeButton: {
    width: 40,
    height: 40,
    borderRadius: 14,
    border: "1px solid #e2e8f0",
    background: "#ffffff",
    cursor: "pointer",
    fontSize: 24,
    lineHeight: 1,
    color: "#0f172a",
  },

  modalBody: {
    padding: 18,
    overflowY: "auto",
  },

  mapFrame: {
    width: "100%",
    height: 430,
    borderRadius: 18,
    overflow: "hidden",
    border: "1px solid #e2e8f0",
  },

  warningBox: {
    padding: 14,
    background: "#fff7ed",
    border: "1px solid #fed7aa",
    borderRadius: 16,
    color: "#9a3412",
  },

  modalStats: {
    display: "grid",
    gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
    gap: 12,
    marginTop: 14,
  },

  addressBox: {
    marginTop: 14,
    padding: 14,
    borderRadius: 16,
    background: "#f8fafc",
    border: "1px solid #e2e8f0",
    color: "#334155",
  },

  modalActions: {
    marginTop: 16,
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
  },

  secondaryButton: {
    padding: "11px 16px",
    border: "1px solid #cbd5e1",
    background: "#ffffff",
    borderRadius: 14,
    cursor: "pointer",
    fontWeight: 800,
  },

  primaryButton: {
    padding: "11px 16px",
    border: "1px solid #f97316",
    background: "#f97316",
    color: "#ffffff",
    borderRadius: 14,
    cursor: "pointer",
    fontWeight: 900,
    boxShadow: "0 10px 20px rgba(249,115,22,0.25)",
  },
};
